import { api, unwrap } from "@lumberset/api-client";
import { ago, initials } from "@lumberset/takeoff-ui";
import {
  Avatar,
  Button,
  Container,
  Drawer,
  EmptyState,
  Field,
  Input,
  SectionHeader,
  Select,
  Table,
  Tabs,
  toast,
} from "@lumberset/ui";
import { MagnifyingGlass, Plus, ShieldCheck, Users } from "@medusajs/icons";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useDeferredValue, useState } from "react";
import { Crumbs } from "../../../components/crumbs";
import { type TeamPerson, teamQuery } from "../../../lib/queries-team";
import { EmailNotConfirmed } from "./-email";
import {
  isStaffRole,
  minutes,
  RoleBadge,
  roleChoices,
  type StaffRoleValue,
  StaffStatus,
} from "./-parts";

export const Route = createFileRoute("/_app/staff/")({
  loader: ({ context: { queryClient } }) => queryClient.ensureQueryData(teamQuery),
  component: StaffList,
});

type View = "current" | "former";

/** The number columns, in the order the cells below fill them. */
const numberColumns = [
  "Reviews finished",
  "Minutes per review",
  "Corrections",
  "Tickets resolved",
  "Ticket replies",
  "Help articles",
  "Price proposals",
  "Reading lessons",
];

function lastActive(person: TeamPerson) {
  if (person.status === "invited") return "Not signed in yet";
  return person.lastActiveAt ? `Active ${ago(person.lastActiveAt)}` : "Not signed in now";
}

const plural = (value: number, one: string, many = `${one}s`) =>
  `${value} ${value === 1 ? one : many}`;

/** How many people are on the team, and how many of them can run it. A deactivated Admin cannot. */
function teamSentence(current: TeamPerson[]) {
  const admins = current.filter(
    (person) => person.role === "admin" && person.status !== "deactivated",
  );
  const active = admins.filter((person) => person.status === "active").length;
  const head = `${plural(current.length, "person", "people")}, ${plural(admins.length, "Admin")}`;
  if (admins.length <= 1) return `${head}. Invite a second Admin so nobody is ever locked out.`;
  if (active <= 1) {
    return `${head}, only ${active === 1 ? "one" : "none"} of them signed in so far.`;
  }
  return `${head}.`;
}

function StaffList() {
  const { data } = useSuspenseQuery(teamQuery);
  const { user: me } = Route.useRouteContext();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const q = useDeferredValue(text.trim().toLowerCase());
  const [view, setView] = useState<View>("current");
  /** The role the invitation starts with, or null while the drawer is closed. */
  const [inviting, setInviting] = useState<StaffRoleValue | null>(null);

  const current = data.staff.filter((person) => isStaffRole(person.role));
  const former = data.staff.filter((person) => !isStaffRole(person.role));
  const shown = (view === "current" ? current : former).filter(
    (person) =>
      !q ||
      person.name.toLowerCase().includes(q) ||
      person.email.toLowerCase().includes(q) ||
      (person.title ?? "").toLowerCase().includes(q),
  );

  return (
    <>
      <Crumbs trail={[{ label: "Staff" }]} />

      <Container className="divide-y p-0">
        <SectionHeader
          level="h1"
          title="Staff"
          description={`The LumberSet people who review takeoffs, answer tickets and run the platform. The numbers cover the last ${data.days} days.`}
          actions={
            <>
              <Button size="small" variant="secondary" onClick={() => setInviting("admin")}>
                <ShieldCheck /> Invite an Admin
              </Button>
              <Button size="small" onClick={() => setInviting("reviewer")}>
                <Plus /> Invite staff
              </Button>
            </>
          }
        />

        <div className="flex flex-col gap-3 px-6 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-col gap-y-2">
            {former.length > 0 ? (
              <Tabs value={view} onValueChange={(value) => setView(value as View)}>
                <Tabs.List>
                  <Tabs.Trigger value="current">Current ({current.length})</Tabs.Trigger>
                  <Tabs.Trigger value="former">Former ({former.length})</Tabs.Trigger>
                </Tabs.List>
              </Tabs>
            ) : null}
            <span className="txt-compact-small text-pretty text-ui-fg-subtle">
              {teamSentence(current)}
            </span>
          </div>
          <div className="sm:w-64">
            <Input
              type="search"
              placeholder="Search by name, email or title"
              prefix={<MagnifyingGlass />}
              value={text}
              onChange={(event) => setText(event.target.value)}
            />
          </div>
        </div>

        {shown.length === 0 ? (
          <EmptyState
            icon={<Users />}
            title={q ? "Nothing matches" : view === "former" ? "No former Staff" : "No Staff yet"}
            description={
              q
                ? "Try a different name or email."
                : "Invite someone and they appear here as soon as the invitation is sent."
            }
          />
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Person</Table.HeaderCell>
                <Table.HeaderCell>Role</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                {numberColumns.map((column) => (
                  <Table.HeaderCell
                    key={column}
                    className="h-auto whitespace-normal py-2 text-right leading-4"
                  >
                    {column}
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {shown.map((person) => (
                <Table.Row
                  key={person.id}
                  data-clickable="true"
                  onClick={() => navigate({ to: "/staff/$id", params: { id: person.id } })}
                >
                  <Table.Cell className="py-2.5">
                    <div className="flex w-[240px] items-center gap-x-3">
                      <Avatar fallback={initials(person.name)} shape="circle" size="small" />
                      <div className="min-w-0">
                        <div className="txt-compact-small-plus truncate text-ui-fg-base">
                          {person.name}
                          {person.id === me.id ? (
                            <span className="txt-compact-xsmall ml-1.5 text-ui-fg-muted">
                              (you)
                            </span>
                          ) : null}
                        </div>
                        <div
                          className="txt-compact-xsmall truncate text-ui-fg-muted"
                          title={person.title ? `${person.title}, ${person.email}` : person.email}
                        >
                          {person.title ? `${person.title}, ${person.email}` : person.email}
                        </div>
                      </div>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <RoleBadge role={person.role} />
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap py-2.5">
                    <div className="flex items-center gap-x-1.5">
                      <StaffStatus status={person.status} />
                      {person.emailVerified ? null : <EmailNotConfirmed />}
                    </div>
                    <div className="txt-compact-xsmall mt-1 text-ui-fg-muted">
                      {lastActive(person)}
                    </div>
                  </Table.Cell>
                  <Table.Cell className="tabular text-right text-ui-fg-base">
                    {person.numbers.reviewsFinished}
                  </Table.Cell>
                  <Table.Cell className="tabular text-right">
                    {minutes(person.numbers.minutesPerReview)}
                  </Table.Cell>
                  <Table.Cell className="tabular text-right">
                    {person.numbers.corrections}
                  </Table.Cell>
                  <Table.Cell className="tabular text-right text-ui-fg-base">
                    {person.numbers.ticketsResolved}
                  </Table.Cell>
                  <Table.Cell className="tabular text-right">
                    {person.numbers.ticketReplies}
                  </Table.Cell>
                  <Table.Cell className="tabular text-right">
                    {person.numbers.articlesWritten}
                  </Table.Cell>
                  <Table.Cell className="tabular text-right">
                    {person.numbers.priceProposals}
                  </Table.Cell>
                  <Table.Cell className="tabular text-right">
                    {person.numbers.lessonsWritten}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Container>

      <Drawer open={inviting !== null} onOpenChange={(open) => !open && setInviting(null)}>
        <Drawer.Content>
          {inviting ? (
            <InviteForm initialRole={inviting} onClose={() => setInviting(null)} />
          ) : null}
        </Drawer.Content>
      </Drawer>
    </>
  );
}

/** Invites someone new, or gives Staff access to someone who already has an account. */
function InviteForm({
  initialRole,
  onClose,
}: {
  initialRole: StaffRoleValue;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [role, setRole] = useState<StaffRoleValue>(initialRole);
  const chosen = roleChoices.find((choice) => choice.value === role);

  const invite = useMutation({
    mutationFn: (form: FormData) => {
      const text = (name: string) => String(form.get(name) ?? "").trim();
      return unwrap(
        api.admin.team.invites.$post({
          json: { name: text("name"), email: text("email"), role, title: text("title") || null },
        }),
      );
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "team"] });
      if (!result.delivered) {
        toast.warning(`${result.person.name} is on the team, but the email did not go out`, {
          description: "Open their page and send it again.",
        });
      } else if (result.sent === "invitation") {
        toast.success(`Invitation sent to ${result.person.email}`, {
          description: "The link to choose a password works for 7 days.",
        });
      } else {
        toast.success(`${result.person.name} now has Staff access`, {
          description: "They already had a LumberSet account, so we told them to sign in.",
        });
      }
      onClose();
    },
    onError: (error) => toast.error(error.message),
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    invite.mutate(new FormData(event.currentTarget));
  }

  return (
    <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
      <Drawer.Header>
        <Drawer.Title>{role === "admin" ? "Invite an Admin" : "Invite staff"}</Drawer.Title>
        <Drawer.Description>
          {role === "admin"
            ? "An Admin runs the platform: Billing Plans, workspaces and their billing, Staff, email, settings and the audit log, and can sign in as any Member or Staff member. "
            : ""}
          They get an email with a link to choose a password. Someone who already has a LumberSet
          account keeps it and is told to sign in.
        </Drawer.Description>
      </Drawer.Header>

      <Drawer.Body className="flex flex-col gap-y-5">
        <Field label="Name">
          {(control) => (
            <Input
              {...control}
              name="name"
              required
              maxLength={120}
              autoComplete="off"
              autoFocus
              placeholder="Sam Ortiz"
            />
          )}
        </Field>
        <Field label="Email">
          {(control) => (
            <Input
              {...control}
              name="email"
              type="email"
              required
              maxLength={254}
              autoComplete="off"
              placeholder="sam@lumberset.com"
            />
          )}
        </Field>
        <Field label="Role" hint={chosen?.hint}>
          {(control) => (
            <Select value={role} onValueChange={(value) => setRole(value as StaffRoleValue)}>
              <Select.Trigger id={control.id} aria-describedby={control["aria-describedby"]}>
                <Select.Value />
              </Select.Trigger>
              <Select.Content>
                {roleChoices.map((choice) => (
                  <Select.Item key={choice.value} value={choice.value}>
                    {choice.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select>
          )}
        </Field>
        <Field label="Title" optional hint="Shown on the Staff list, for example Senior estimator.">
          {(control) => <Input {...control} name="title" maxLength={120} autoComplete="off" />}
        </Field>
      </Drawer.Body>

      <Drawer.Footer>
        <Button type="button" variant="secondary" size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" size="small" isLoading={invite.isPending}>
          Send invitation
        </Button>
      </Drawer.Footer>
    </form>
  );
}
